import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { RecordContextProvider } from 'react-admin';
import { CardTable } from 'shared/mui/Card/CardTableNew/CardTable';
import { Link } from 'shared/mui/Link/Link';
import { Stack } from 'shared/mui/Stack/Stack';
import { TableRowWrapper } from 'shared/mui/Table/TableRowWrapperNew/TableRowWrapper';
import {
  BooleanField,
  ChipField,
  DateTimeField,
  EmbeddedFunctionField,
  LastActiveField,
  TextField,
} from 'shared/react-admin/Fields';
import { EmbeddedArrayField } from 'shared/react-admin/Fields/Embedded/EmbeddedArrayField/EmbeddedArrayField';
import { MerchantAccount } from 'types/embedded/merchantAccount';

type OmitField = 'merchant';

type Props = {
  onClose?: VoidFunction;
  merchantAccount?: MerchantAccount;
  handleOpenCredentialsDialog?: (record: MerchantAccount) => void;
  omit?: OmitField[];
};

export const MerchantAccountShowDialogTable = ({
  onClose,
  merchantAccount,
  handleOpenCredentialsDialog,
  omit,
}: Props) => {
  const translate = useTypedTranslate();
  const isOmitted = (field: OmitField) => omit?.includes(field);

  return (
    <CardTable tableBodyProps={{ sx: { '& td': { width: '212px' } } }}>
      <TableRowWrapper headerStyle={{ width: '212px' }} label="ID">
        <TextField source="originId" />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.table.active')}>
        <BooleanField source="active" />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.table.name')}>
        <TextField source="name" />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.table.lastActivity')}>
        <LastActiveField
          label={translate('app.table.lastActivity')}
          merchantId={merchantAccount?.merchants.at(-1)?.['@id']}
        />
      </TableRowWrapper>
      {!isOmitted('merchant') && (
        <TableRowWrapper label={translate('app.table.merchant')}>
          <EmbeddedArrayField
            render={(values) => {
              return values.map((v) => v.name);
            }}
            sourceField="merchants"
          />
        </TableRowWrapper>
      )}
      <TableRowWrapper label={translate('app.table.provider')}>
        <EmbeddedFunctionField sourceField="provider.name" />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.table.currency')}>
        <EmbeddedFunctionField sourceField="currency.alphaCode" />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.table.gateway')}>
        <EmbeddedArrayField
          render={(values) => {
            return (
              <Stack direction="row" flexWrap="wrap" gap="4px">
                {values.map((v) => {
                  return (
                    <RecordContextProvider key={v['@id']} value={v}>
                      <ChipField color="primary" source="name" />
                    </RecordContextProvider>
                  );
                })}
              </Stack>
            );
          }}
          sourceField="gateways"
        />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.merchantAccount.partners')}>
        <BooleanField source="partner" />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.merchantAccount.merchantIdEnabled')}>
        <BooleanField source="merchantIdEnabled" />
      </TableRowWrapper>
      {!!handleOpenCredentialsDialog && (
        <TableRowWrapper label={translate('app.merchantAccount.credentials')}>
          <Link
            onClick={() => {
              merchantAccount && handleOpenCredentialsDialog(merchantAccount);
              onClose?.();
            }}
            sx={{ cursor: 'pointer' }}
          >
            {translate('app.buttons.showValue')}
          </Link>
        </TableRowWrapper>
      )}
      <TableRowWrapper label={translate('app.table.createdAt')}>
        <DateTimeField inline source="createdAt" />
      </TableRowWrapper>
      <TableRowWrapper label={translate('app.table.updatedAt')}>
        <DateTimeField inline source="updatedAt" />
      </TableRowWrapper>
    </CardTable>
  );
};
